import { z } from "zod";

/**
 * ADR-0017: roles are named sets of permissions over the portal's
 * sections, the things inside them and verbs; a person's access on an
 * environment is the union of the roles they hold there. This file is
 * the tree - the contract customers build roles on. Adding a resource is
 * additive; renaming or removing one is `Breaking` in the notes.
 *
 * A permission is `section:verb` (the section's own switches - `enabled`
 * says whether the section can be seen at all) or `section.thing:verb`.
 * A thing's verb grants nothing while its section is not enabled.
 */
export const PERMISSION_TREE = {
  environment: { verbs: ["enabled", "edit"], things: { chain: ["edit"] } },
  alarms: { verbs: ["enabled"], things: {} },
  trusted_advisor: { verbs: ["enabled"], things: {} },
  support_cases: { verbs: ["enabled"], things: {} },
  quotas: { verbs: ["enabled", "request"], things: {} },
  concurrency: { verbs: ["enabled"], things: {} },
  journeys: {
    verbs: ["enabled"],
    things: { paths: ["view"], contacts: ["view", "inspect"], transcript: ["view"], agents: ["view"] },
  },
  configuration: {
    verbs: ["enabled"],
    things: {
      profiles: ["view", "create", "edit", "delete"],
      announcements: ["view", "create", "edit", "delete"],
      kv: ["view", "create", "edit", "delete"],
      hours: ["view", "create", "edit", "delete"],
      menus: ["view", "create", "edit", "delete"],
      voice: ["view", "edit"],
      bindings: ["view", "edit"],
      overrides: ["view", "edit"],
      aliases: ["view", "edit"],
      promotions: ["view", "create", "approve"],
    },
  },
  audit: { verbs: ["enabled", "undo"], things: {} },
  /** #568: Slack notifications. `enabled` reads where an environment's notices go; `route` connects channels and routes to them. */
  notifications: { verbs: ["enabled", "route"], things: {} },
  users: { verbs: ["enabled", "create", "edit", "delete"], things: {} },
  roles: { verbs: ["enabled", "create", "edit", "delete"], things: {} },
} as const;

export type PermissionSection = keyof typeof PERMISSION_TREE;
export type PermissionVerb = "enabled" | "view" | "edit" | "create" | "delete" | "approve" | "request" | "inspect" | "undo" | "route";

/** Every permission the tree names, in tree order - what a role's `permissions` is validated against and what the matrix draws. */
export const ALL_PERMISSIONS: readonly string[] = Object.entries(PERMISSION_TREE).flatMap(([section, node]) => [
  ...node.verbs.map((v) => `${section}:${v}`),
  ...Object.entries(node.things as Record<string, readonly string[]>).flatMap(([thing, verbs]) => verbs.map((v) => `${section}.${thing}:${v}`)),
]);

export const PermissionSchema = z.string().refine((p) => ALL_PERMISSIONS.includes(p), { message: "not a permission the portal knows" });
export type Permission = string;

/** `section.thing:verb` → its parts; `section:verb` has no thing. */
export function parsePermission(p: string): { section: string; thing?: string; verb: string } {
  const [resource, verb] = p.split(":") as [string, string];
  const [section, thing] = resource.split(".") as [string, string?];
  return { section, ...(thing ? { thing } : {}), verb };
}

/** The lower-case labels the matrix shows for a section, a thing and a verb (sentence case is applied on screen). */
export const PERMISSION_LABELS: Record<string, string> = {
  environment: "environment", "environment.chain": "environment chain",
  alarms: "alarms", trusted_advisor: "trusted advisor", support_cases: "support cases", quotas: "service quotas", concurrency: "concurrency",
  journeys: "journeys", "journeys.paths": "paths", "journeys.contacts": "contacts and search", "journeys.transcript": "transcript and recording", "journeys.agents": "agent names at a minute",
  configuration: "flow configuration", "configuration.profiles": "profiles and versions", "configuration.announcements": "announcements", "configuration.kv": "key-value pairs", "configuration.hours": "hours and holidays", "configuration.menus": "menus", "configuration.voice": "voice", "configuration.bindings": "aliases (numbers)", "configuration.overrides": "overrides and emergency close", "configuration.aliases": "reference aliases", "configuration.promotions": "promotions",
  audit: "audit trail", notifications: "Slack notifications", users: "people", roles: "roles and groups",
  enabled: "enabled", view: "read", edit: "edit", create: "create", delete: "delete", approve: "approve", request: "request", inspect: "inspect", undo: "undo", route: "connect and route",
};

const all = (pred: (p: string) => boolean) => ALL_PERMISSIONS.filter(pred);
const everyEnabled = all((p) => p.endsWith(":enabled"));
const everyView = all((p) => p.endsWith(":view"));

/**
 * The three built-in roles every organization has, and cannot delete or
 * edit, only copy. `viewer`: read everything a customer may see, inspect
 * nothing. `admin`: what ADR-0008's admin could do - author configuration,
 * prepare promotions, set overrides. `manager`: all of that plus approve,
 * the chain and the environment's switches, people, roles, undo, and the
 * journeys content only a manager sees by default (ADR-0016's answer).
 */
export const BUILT_IN_ROLES: Record<"viewer" | "admin" | "manager", { name: string; description: string; permissions: readonly string[] }> = {
  viewer: {
    name: "Viewer",
    description: "Reads every section a customer may see. Changes nothing, inspects nothing. What an invitee holds until given more.",
    permissions: [...everyEnabled, ...everyView],
  },
  admin: {
    name: "Admin",
    description: "Authors flow configuration, prepares promotions, sets and ends overrides, requests quota increases, connects Slack and routes notifications to it. Cannot approve, cannot change people or roles.",
    permissions: [
      ...everyEnabled,
      ...everyView,
      "quotas:request",
      // #568: connecting a Slack channel and routing this environment's notices to it.
      "notifications:route",
      ...all((p) => p.startsWith("configuration.") && /:(create|edit|delete)$/.test(p) && !p.startsWith("configuration.promotions")),
      "configuration.promotions:create",
    ],
  },
  manager: {
    name: "Manager",
    description: "Everything an Admin does, and: approves changes, sets the environment's switches and the chain, manages people and roles, undoes from the audit trail, opens a call's trace and transcript, sees agent names.",
    permissions: [...ALL_PERMISSIONS],
  },
};
export const BUILT_IN_ROLE_IDS = Object.keys(BUILT_IN_ROLES) as Array<keyof typeof BUILT_IN_ROLES>;
/** The organization built-in roles live under; read by every organization. */
export const BUILT_IN_ORGANISATION = "__builtin__";

/** Staff roles (ADR-0017 section 4), kept on the staff member's PortalUsers row. */
export const StaffRoleSchema = z.enum(["gairm-staff", "gairm-reader", "gairm-superadmin"]);
export type StaffRole = z.infer<typeof StaffRoleSchema>;
/** A reader holds enabled and view on everything, inspect without the transcript. */
export const READER_PERMISSIONS: readonly string[] = [...everyEnabled, ...everyView.filter((p) => p !== "journeys.transcript:view"), "journeys.contacts:inspect"];

/**
 * ADR-0017 section 4 as amended 2026-09-24 (#371): approving a customer's
 * change is the customer's managers' job. Staff approve only from inside
 * that customer's portal with editing enabled, where they hold the
 * customer's `manager` (ADR-0011) and the decision is recorded as staff
 * acting for the customer. In their own portal these are withheld from
 * every staff role, so Focus, the decision route and the Promotions page
 * all follow from the one set.
 */
export const STAFF_WITHHELD: readonly string[] = ["configuration.promotions:approve"];

/** What a staff role holds in the staff member's own portal - never inside view-as, which is the customer's built-in. */
export function staffPermissionList(role: StaffRole): readonly string[] {
  return role === "gairm-reader" ? READER_PERMISSIONS : ALL_PERMISSIONS.filter((p) => !STAFF_WITHHELD.includes(p));
}

/**
 * The union rule (ADR-0017 section 2): a set of permissions with the one
 * question every route asks. A disabled section grants nothing beneath it.
 */
export class PermissionSet {
  private readonly set: Set<string>;
  constructor(permissions: Iterable<string>, readonly grantedBy: Record<string, string[]> = {}) {
    this.set = new Set(permissions);
  }
  has(permission: string): boolean {
    if (!this.set.has(permission)) return false;
    const { section, verb } = parsePermission(permission);
    return verb === "enabled" || this.set.has(`${section}:enabled`);
  }
  hasAny(permissions: readonly string[]): boolean {
    return permissions.some((p) => this.has(p));
  }
  /** Every permission that is actually in force, the disabled sections' children removed. */
  list(): string[] {
    return ALL_PERMISSIONS.filter((p) => this.has(p));
  }
  static everything(): PermissionSet {
    return new PermissionSet(ALL_PERMISSIONS);
  }
  static none(): PermissionSet {
    return new PermissionSet([]);
  }
}

/**
 * Which permission a profile field type needs for a verb - the per-type
 * rows of the configuration section. A type without that verb in the tree
 * (voice has read and edit only) falls back to its `edit`, so a create or
 * delete of a voice field is an edit of the profile's voice.
 */
export function fieldPermission(fieldType: string, verb: "view" | "edit" | "create" | "delete"): string {
  const thing = ({ announcement: "announcements", kv: "kv", hours: "hours", menu: "menus", voice: "voice" } as Record<string, string>)[fieldType] ?? "profiles";
  const exact = `configuration.${thing}:${verb}`;
  return ALL_PERMISSIONS.includes(exact) ? exact : `configuration.${thing}:edit`;
}
